// Where a client-side navigation should leave the viewport.
//
// A real browser navigation starts you at the top of the new document. A
// client-side one does not: the DOM is swapped underneath a window that keeps
// its scroll offset, so you arrive wherever the previous page happened to be.
//
// That is #58 — "when entering the funzone, the page loads at the bottom". The
// Funzone link sits at the bottom of a long home page, so you scroll down to
// click it and land the same distance down the next screen. Nothing about
// Funzone is special; /windows -> /regions does it too, and every screen added
// later would have inherited it.
//
// Split out from App.tsx so the decision is testable without a DOM: the router
// supplies the hash and a way to look up an element, and this says where to go.

export type ScrollTarget =
  | { kind: 'top' }
  /** The id of an element to bring into view — a `/page#section` link. */
  | { kind: 'element'; id: string }
  /** Leave the viewport alone (browser back/forward restores its own offset). */
  | { kind: 'none' }

export interface ScrollDecisionInput {
  /** `location.hash`, with or without the leading '#'. */
  hash: string
  /** True when the router pushed this navigation; false for back/forward. */
  pushed: boolean
  /** Does an element with this id exist in the freshly rendered page? */
  hasElement: (id: string) => boolean
}

/**
 * Decide where a navigation should scroll.
 *
 * Back/forward returns `none` on purpose. The browser's own `scrollRestoration`
 * is 'auto' by default and puts you back where you were, which is what people
 * expect from a back button — forcing the top there would be a second bug wearing
 * the first one's clothes.
 *
 * A hash that matches nothing falls through to the top rather than doing nothing,
 * so a stale `#packing-list` link cannot strand someone mid-page.
 */
export function decideScroll(input: ScrollDecisionInput): ScrollTarget {
  if (!input.pushed) return { kind: 'none' }

  const id = input.hash.replace(/^#/, '')
  if (id && input.hasElement(id)) return { kind: 'element', id }

  return { kind: 'top' }
}
